import React from 'react';
import PropTypes from 'prop-types';
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Flex, Text } from 'atoms';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const TopRow = styled(Flex)`
	align-items: center;
	gap: 48px;
	margin-bottom: 24px;
`;

const IDField = styled(Box)`
	display: flex;
	align-items: baseline;
	gap: 8px;
`;

const IDLabel = styled(Text)`
	font-size: 14px;
	color: #666;
	font-weight: 400;
`;

const IDValue = styled(Text)`
	font-size: 16px;
	color: #1890ff;
	font-weight: 600;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
`;

const ActionSection = styled(Flex)`
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
`;

const ViewSection = styled(Flex)`
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	padding: 0 12px;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	background: #e6f7ff;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	color: #000;
	font-size: 16px;
	transition: background 0.2s ease;

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	padding: 8px 18px;
	min-width: 110px;

	background: #fff;
	color: #52c41a;

	border: 1.5px solid #52c41a;
	border-radius: 6px;

	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	transition: all 0.25s ease;

	.anticon {
		font-size: 16px;
		color: #52c41a;
	}

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}

	&:active {
		background: #d9f7be;
		border-color: #52c41a;
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	min-width: 110px;

	.anticon {
		font-size: 16px;
	}

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
		transform: translateY(-1px);
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
		transform: translateY(0);
	}
`;

const formatDateTime = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY HH:mm:ss');
};

export default function AdHocRequestCardV2({ tripRequest, onView, onApprove, onReject }) {
	return (
		<RequestCard>
			<TopRow>
				<IDField>
					<IDLabel>Trip ID :</IDLabel>
					<IDValue onClick={() => onView(tripRequest.id)}>
						{tripRequest.id || '-'}
					</IDValue>
				</IDField>
				<IDField>
					<IDLabel>Req ID :</IDLabel>
					<IDValue onClick={() => onView(tripRequest.id)}>
						{tripRequest.req_id || tripRequest.id || '-'}
					</IDValue>
				</IDField>
			</TopRow>

			<CardBody>
				<InfoColumns>
					<InfoField>
						<FieldLabel>Project Name :</FieldLabel>
						<FieldValue>{tripRequest.project_name || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Vehicle Type :</FieldLabel>
						<FieldValue>{tripRequest.vehicle_type || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Start at :</FieldLabel>
						<FieldValue>{formatDateTime(tripRequest.start_at)}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Vendor Name :</FieldLabel>
						<FieldValue>{tripRequest.vendor_name || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Vehicle Number :</FieldLabel>
						<FieldValue>{tripRequest.vehicle_number || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Created by :</FieldLabel>
						<FieldValue>{tripRequest.created_by || '-'}</FieldValue>
					</InfoField>
				</InfoColumns>
				<ViewSection>
					<ViewButton onClick={() => onView(tripRequest.id)}>
						<EyeOutlined />
					</ViewButton>
				</ViewSection>
				<ActionSection>
					<ApproveButton onClick={() => onApprove(tripRequest.id)}>
						<CheckOutlined />
						Approve
					</ApproveButton>
					<RejectButton onClick={() => onReject(tripRequest.id)}>
						<CloseOutlined />
						Reject
					</RejectButton>
				</ActionSection>
			</CardBody>
		</RequestCard>
	);
}

AdHocRequestCardV2.propTypes = {
	tripRequest: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		req_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		project_name: PropTypes.string,
		vehicle_type: PropTypes.string,
		vendor_name: PropTypes.string,
		vehicle_number: PropTypes.string,
		start_at: PropTypes.string,
		created_by: PropTypes.string,
	}).isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
};
